import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Minus, Plus } from "lucide-react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSitePath } from "../context/SiteContext";
import { api, money, toCriteria } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { StepLayout } from "../components/StepLayout";

const UNIT = { per_stay: "per stay", per_guest: "per guest", per_guest_night: "per guest, per night" };

const ServiceCard = ({ s, currency, qty, setQty }) => (
  <article className={`flex flex-col overflow-hidden rounded-2xl border bg-white transition-colors ${qty ? "border-brand" : "border-brand/10"}`} data-testid={`service-card-${s.id}`}>
    {s.image && <img src={s.image} alt={s.name} className="h-40 w-full object-cover" />}
    <div className="flex flex-1 flex-col p-5">
      <h3 className="text-lg font-medium text-ink">{s.name}</h3>
      <p className="mt-1 flex-1 text-sm text-ink/60">{s.description}</p>
      <div className="mt-4 flex items-center justify-between">
        <p className="font-mono text-ink">{money(s.price, currency)} <span className="text-xs text-ink/50">{UNIT[s.unit]}</span></p>
        <div className="flex items-center gap-2">
          <button className="qty-btn" disabled={!qty} onClick={() => setQty(qty - 1)} data-testid={`service-dec-${s.id}`}><Minus className="h-3.5 w-3.5" /></button>
          <span className="w-6 text-center font-mono" data-testid={`service-qty-${s.id}`}>{qty}</span>
          <button className="qty-btn" onClick={() => setQty(qty + 1)} data-testid={`service-inc-${s.id}`}><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  </article>
);

export default function Services() {
  const { criteria, hotel, rate, services, setService } = useBooking();
  const navigate = useNavigate();
  const sp = useSitePath();
  const { data } = useQuery({
    queryKey: ["services", hotel?.id, criteria],
    queryFn: () => api.post(`/hotels/${hotel.id}/services`, toCriteria(criteria)).then((r) => r.data),
    enabled: !!hotel,
  });
  if (!hotel || !rate) return <Navigate to={sp("/hotels")} replace />;
  const actions = (
    <div className="flex gap-3">
      <button className="btn-outline flex-1" onClick={() => navigate(sp(`/rates/${hotel.id}`))} data-testid="services-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
      <button className="btn-primary flex-1" onClick={() => navigate(sp("/details"))} data-testid="services-continue-button">Continue<ArrowRight className="h-4 w-4" /></button>
    </div>
  );
  return (
    <StepLayout step={3} sidebarActions={actions} mobileBar={{ onClick: () => navigate(sp("/details")) }}>
      <h1 className="font-serif text-4xl text-ink sm:text-5xl">Enhance your stay</h1>
      <p className="mt-2 text-sm text-ink/60">Optional extras. Add as many as you like, or skip this step.</p>
      <div className="mt-8 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="services-list">
        {data?.services.map((s) => (
          <ServiceCard key={s.id} s={s} currency={data.currency} qty={services[s.id] || 0} setQty={(q) => setService(s.id, q)} />
        ))}
      </div>
      {data && data.services.length === 0 && <p className="mt-4 rounded-2xl bg-white p-6 text-sm text-ink/60" data-testid="services-empty">No extras are offered for this stay. Continue to your details.</p>}
    </StepLayout>
  );
}
